/* eslint-disable no-restricted-globals */

import store from "src/utils/redux/store";
import { captureException } from "src/utils/sentry";
import {
  isDevMode,
  isInAppNativePurchaseEligible,
} from "src/utils/userSegment/features";
import goto from ".";
import { isBuyflow } from "./buyflow";
import { pushReroll, setParam } from "./utils";
import { isInApp } from "@utils/userSegment";

declare const Android: any;

// "/purchase/:planId"
// "/purchase/(en|es|de)/:planId"
// "/purchase-hm/:planId"
function isPurchase() {
  return /^\/purchase(-hm_)?\/?/.test(location.pathname);
}
function purchaseRoot(isHM: boolean, planId?: string) {
  const {
    language,
    serverContext,
    userData,
    geoLocation,
    routeId,
    recommendedPlan,
  } = store.getState();

  const root = `/purchase${isHM ? "-hm" : ""}`;

  const pathMatch = /^\/purchase(?:-hm)?(?:\/(?:en|es|de))?\/(.*)/.exec(
    location.pathname
  );
  const finalPlanId = planId || recommendedPlan.noom_plan_id || pathMatch?.[1];

  /* istanbul ignore next sanity */
  if (!finalPlanId) {
    throw new Error("No plan id provided");
  }

  let purchaseUrl =
    serverContext.PURCHASE_URL ??
    (isHM ? `${root}/` : `${root}/${language || "en"}/`);
  purchaseUrl += finalPlanId;

  const queryParams = new URLSearchParams();

  // Add route to purchase page url params since cookies don't work 100% of the time.
  setParam(queryParams, "route", routeId);

  // Add fcc if exists, for the in-app buyflows, in order to enforce location based on device locale.
  if (geoLocation?.is_forced_country_code) {
    setParam(queryParams, "fcc", geoLocation.country_code);
  }

  const currentUrlParams = new URLSearchParams(window.location.search);
  currentUrlParams.forEach((value, key) => {
    queryParams.set(key, value);
  });

  if (isInApp()) {
    setParam(queryParams, "email", userData.email);
  }

  if (isDevMode()) {
    setParam(queryParams, "idm", true);
  }
  return `${purchaseUrl.replace(/\/$/, "")}/${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
}

export const linkTo = {};

export default {
  purchaseSurvey(planId?: string, question?: string) {
    const url = `${purchaseRoot(false, planId)}#/createAccount${
      question ? `/${question}` : ""
    }`;
    pushReroll(url, () => isPurchase() || isBuyflow());
  },
  hmPurchaseSurvey(planId: string) {
    pushReroll(
      `${purchaseRoot(true, planId)}#/createAccount`,
      () => isPurchase() || isBuyflow()
    );
  },

  welcomeToNoom(isHM: boolean) {
    pushReroll(`${purchaseRoot(isHM)}#/welcomeToNoom`, isPurchase);
  },
  account(isHM: boolean) {
    pushReroll(`${purchaseRoot(isHM)}#/account`, isPurchase);
  },
  finalUpdateGraph() {
    pushReroll(`${purchaseRoot(false)}#/finalUpdateGraph`, isPurchase);
  },
  buildPlan() {
    pushReroll(`${purchaseRoot(false)}#/buildPlan`, isPurchase);
  },
  chooseTrialPrice(isHM: boolean) {
    pushReroll(`${purchaseRoot(isHM)}#/chooseTrialPrice`, isPurchase);
  },
  payment(isHM: boolean) {
    pushReroll(`${purchaseRoot(isHM)}#/payment`, isPurchase);
  },
  paymentDetails(isHM: boolean) {
    pushReroll(`${purchaseRoot(isHM)}#/details`, isPurchase);
  },

  nativePurchase() {
    if ((window as any).webkit?.messageHandlers) {
      try {
        // NOTE(norbert): the buyflowPurchaseNoDetails endpoint was only introduced
        // in the iOS 9.14 release, so we need to check if it exists before calling it
        if (
          isInAppNativePurchaseEligible() &&
          (window as any).webkit.messageHandlers.buyflowPurchaseNoDetails
        ) {
          // If app and route support web purchase, use that
          goto.buildPlan();
        } else {
          (window as any).webkit.messageHandlers.buyflowPurchase.postMessage(
            {}
          );
        }
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.ios");
      }
    }

    if (typeof Android !== "undefined") {
      try {
        Android.buyflowPurchase();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchase: no message handler found");

    return false;
  },
  nativePurchaseNoDetails() {
    if (
      isInAppNativePurchaseEligible() &&
      (window as any).webkit?.messageHandlers?.buyflowPurchaseNoDetails
    ) {
      try {
        (
          window as any
        ).webkit.messageHandlers.buyflowPurchaseNoDetails.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseNoDetails.ios");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseNoDetails: no message handler found");

    return false;
  },
  nativePurchaseComplete() {
    if ((window as any).webkit?.messageHandlers) {
      try {
        (
          window as any
        ).webkit.messageHandlers.buyflowPurchaseCompleted.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.ios");
      }
    }
    if (typeof Android !== "undefined") {
      try {
        Android.buyflowPurchaseCompleted();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseComplete: no message handler found");
    return false;
  },
};
